import Tippy from '@tippyjs/react';
import styled, { css } from 'styled-components';
import { palette, theme } from 'styled-tools';

import { units } from '../../utils/spacing';
import { fontSize, FontSizes, fontWeight, FontWeights } from '../../utils/typography';
import { TextAlignment } from '../Text/types';
import { PaletteColor } from '../ThemeProvider';

export interface TooltipCssProps {
	$backgroundColor: PaletteColor;
	$textColor: PaletteColor;
	$textAlign?: TextAlignment;
	$maxWidth?: number | string;
	$showArrow?: boolean;
}

const tooltipCss = css<TooltipCssProps>`
	background: ${p => palette(p.$backgroundColor)};
	color: ${p => palette(p.$textColor)};
	padding: ${units(1)};
	${fontSize(FontSizes.sm)};
	${fontWeight(FontWeights.Medium)};
	border-radius: ${theme('tooltips.borderRadius')};
	${({ $textAlign }) => ($textAlign ? `text-align: ${$textAlign}` : '')};
`;

export const StyledTooltipBody = styled.div<TooltipCssProps>`
	${tooltipCss}
	width: fit-content;
	${({ $maxWidth }): string => ($maxWidth ? `max-width: ${$maxWidth}px` : '')};
`;

export const StyledTooltip = styled(Tippy)<TooltipCssProps>`
	${/** adapted from 'tippy.js/dist/tippy.css' */ ''}
	position: relative;
	white-space: normal;
	outline: 0;
	transition-property: transform, visibility, opacity;
	&[data-animation='fade'][data-state='hidden'] {
		opacity: 0;
	}
	[data-tippy-root] {
		max-width: calc(100vw - 10px);
	}

	--arrow-size: 5px;
	.tippy-content {
		padding: 0;
	}
	.tippy-arrow {
		width: 16px;
		height: 16px;
		color: ${p => palette(p.$backgroundColor)};
		display: ${p => (p.$showArrow ? 'block' : 'none')};
	}
	.tippy-arrow:before {
		content: '';
		position: absolute;
		border-color: transparent;
		border-style: solid;
		border-width: var(--arrow-size);
	}
	&[data-placement^='top'] > .tippy-arrow {
		bottom: 0;
	}
	&[data-placement^='top'] > .tippy-arrow:before {
		left: 0;
		border-width: 8px 8px 0;
		border-top-color: initial;
		transform-origin: center top;
		bottom: calc((var(--arrow-size) * -1) + 1px);
	}
	&[data-placement^='bottom'] > .tippy-arrow {
		top: 0;
	}
	&[data-placement^='bottom'] > .tippy-arrow:before {
		left: 0;
		border-width: 0 8px 8px;
		border-bottom-color: initial;
		transform-origin: center bottom;
		top: calc((var(--arrow-size) * -1) + 1px);
	}
	&[data-placement^='left'] > .tippy-arrow {
		right: 0;
	}
	&[data-placement^='left'] > .tippy-arrow:before {
		border-width: 8px 0 8px 8px;
		border-left-color: initial;
		transform-origin: center left;
		right: calc((var(--arrow-size) * -1) + 1px);
	}
	&[data-placement^='right'] > .tippy-arrow {
		left: 0;
	}
	&[data-placement^='right'] > .tippy-arrow:before {
		border-width: 8px 8px 8px 0;
		border-right-color: initial;
		transform-origin: center right;
		left: calc((var(--arrow-size) * -1) + 1px);
	}
	&[data-inertia][data-state='visible'] {
		transition-timing-function: cubic-bezier(0.54, 1.5, 0.38, 1.11);
	}

	${tooltipCss}
`;
